import React, { useEffect, useMemo } from 'react';
import UseCompanyList from 'cms/features/feature_job/hooks/UseCompanyList';
import CompanyItem from './CompanyItem';
import {
    Box,
    Grid,
    Typography,
    CircularProgress,
    Alert
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import { useTranslation } from 'core/context/TranslationContext';

function CompanyList({ searchFilters = {}, onLoadingChange }) {
    const filters = useMemo(() => searchFilters, [JSON.stringify(searchFilters)]);
    const { data, isLoading, isError, isFetching } = UseCompanyList({ size: 30, filters });
    const companies = data?.results ?? [];
    const total = data?.count ?? companies.length;

    useEffect(() => {
        if (onLoadingChange) onLoadingChange(isLoading || isFetching);
    }, [isLoading, isFetching, onLoadingChange]);

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (isError) {
        return (
            <Alert severity="error" sx={{ mt: 2 }}>
                Unable to load companies. Please try again.
            </Alert>
        );
    }

    if (companies.length === 0) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 1 }}>
                <BusinessIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                <Typography variant="body1" color="text.secondary">
                    No employers found. Try a different search.
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {total} employer{total !== 1 ? 's' : ''} found
            </Typography>
            <Grid container spacing={2}>
                {companies.map((company) => (
                    <Grid item key={company.id} xs={12} sm={6} lg={4}>
                        <CompanyItem company={company} />
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
}

export default CompanyList;
